import {Regions} from "./../model/Region"
import {UniversityBranches} from "./../model/UniversityBranch"
import {Specialities} from "./../model/Speciality"
import {Demand, DemandList} from "../model/Demand"
import {Divisions} from "../model/Division"

import {composeSheetData, SheetData} from "./../Excel/export"
import {lpad, composeRow, RowData, getCompany, getSpeciality, getRegion, getUniversityBranch} from "./../model/utils/utils"
import {User} from "../model/User";
import RowStyle = XlsxStyle.RowStyle;
import {Student} from "../model/Student";

enum Rows {
    Title1 = 0,
    Title2,
    Company,
    Date,
    Person,
    Blank,
    Heading1,
    Heading2,
    Heading3,
}

enum Columns {
    id = 0,
    division,
    company,
    companyRegion,
    companyCity,
    specialityCategory,
    specialityCode,
    specialityType,
    courseOfStudy,
    specialityTitle,
    specialityPriority,
    nationalProject,
    englishLevel,
    language,
    entranceYear,
    master,
    period,
    contract,
    age,
    targetOpk,
    graduateYear,
    studentPeriod,
    details,
    universityAbbreviation,
    universityTitle,
    universityType,
    region,
    district,
}

const makeEmptyRow = <D = RowData>(data: D = {} as D): D => ({
    [Columns.id]: null,
    [Columns.division]: null,
    [Columns.company]: null,
    [Columns.companyRegion]: null,
    [Columns.companyCity]: null,
    [Columns.specialityCategory]: null,
    [Columns.specialityCode]: null,
    [Columns.specialityType]: null,
    [Columns.courseOfStudy]: null,
    [Columns.specialityTitle]: null,
    [Columns.specialityPriority]: null,
    [Columns.entranceYear]: null,
    [Columns.master]: null,
    [Columns.period]: null,
    [Columns.nationalProject]: null,
    [Columns.englishLevel]: null,
    [Columns.language]: null,
    [Columns.contract]: null,
    [Columns.age]: null,
    [Columns.targetOpk]: null,
    [Columns.graduateYear]: null,
    [Columns.studentPeriod]: null,
    [Columns.details]: null,
    [Columns.universityAbbreviation]: null,
    [Columns.universityTitle]: null,
    [Columns.universityType]: null,
    [Columns.region]: null,
    [Columns.district]: null,
    ...data,
})

const merges = [
    [Columns.id, Rows.Title1, Columns.district, Rows.Title1],
    [Columns.id, Rows.Title2, Columns.district, Rows.Title2],
    [Columns.id, Rows.Company, Columns.division, Rows.Company],
    [Columns.id, Rows.Date, Columns.division, Rows.Date],
    [Columns.id, Rows.Person, Columns.division, Rows.Person],

    [Columns.id,                 Rows.Heading1, Columns.id,                 Rows.Heading3],
    [Columns.division,           Rows.Heading1, Columns.division,           Rows.Heading3],
    [Columns.company,            Rows.Heading1, Columns.company,            Rows.Heading3],
    [Columns.companyRegion,      Rows.Heading1, Columns.companyRegion,      Rows.Heading3],
    [Columns.companyCity,        Rows.Heading1, Columns.companyCity,        Rows.Heading3],
    [Columns.specialityCategory, Rows.Heading1, Columns.specialityCategory, Rows.Heading3],

    [Columns.specialityCode,      Rows.Heading1, Columns.specialityPriority,  Rows.Heading2],

    [Columns.entranceYear, Rows.Heading1, Columns.entranceYear, Rows.Heading3],
    [Columns.master,       Rows.Heading1, Columns.master,       Rows.Heading3],
    [Columns.period,       Rows.Heading1, Columns.period,       Rows.Heading3],
    [Columns.nationalProject, Rows.Heading1, Columns.nationalProject, Rows.Heading3],
    [Columns.englishLevel, Rows.Heading1, Columns.englishLevel, Rows.Heading3],
    [Columns.language,     Rows.Heading1, Columns.language,     Rows.Heading3],

    [Columns.contract,        Rows.Heading1, Columns.details,      Rows.Heading2],

    [Columns.universityAbbreviation, Rows.Heading1, Columns.universityType, Rows.Heading2],

    [Columns.region,   Rows.Heading1, Columns.region,   Rows.Heading3],
    [Columns.district, Rows.Heading1, Columns.district, Rows.Heading3],
]

let widths = makeEmptyRow({
    [Columns.id]: 6.5,
    [Columns.division]: 10.83,
    [Columns.company]: 24.83,
    [Columns.companyRegion]: 24.83,
    [Columns.companyCity]: 24.83,
    [Columns.specialityCategory]: 7.67,
    [Columns.specialityCode]: 14.33,
    [Columns.specialityType]: 12.67,
    [Columns.courseOfStudy]: 16.33,
    [Columns.specialityTitle]: 24,
    [Columns.specialityPriority]: 25.67,
    [Columns.entranceYear]: 11,
    [Columns.master]: 11,
    [Columns.period]: 12.67,
    [Columns.nationalProject]: 16.33,
    [Columns.englishLevel]: 16.33,
    [Columns.language]: 16.33,
    [Columns.contract]: 25.67,
    [Columns.age]: 11.5,
    [Columns.targetOpk]: 11.5,
    [Columns.graduateYear]: 15.33,
    [Columns.studentPeriod]: 10.17,
    [Columns.details]: 27.5,
    [Columns.universityAbbreviation]: 17.5,
    [Columns.universityTitle]: 19,
    [Columns.universityType]: 19,
    [Columns.region]: 19,
    [Columns.district]: 18.5,
})


const border = {
    top:    {style: "thin", color: {auto: 1}},
    left:   {style: "thin", color: {auto: 1}},
    right:  {style: "thin", color: {auto: 1}},
    bottom: {style: "thin", color: {auto: 1}},
}

const title1Row = (): RowData => makeEmptyRow({
    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "center", vertical: "center"}}, v: "Данные о лицах, с которыми заключен договор"}
})

const title2Row = (): RowData => makeEmptyRow({
    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "center", vertical: "center"}}, v: "о целевом обучении в отчетном году"}
})

const companyRow = (v: string): RowData => makeEmptyRow({
    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "right",  vertical: "top"}},    v: "Организация:"},
    [Columns.company]: {s: {font: {bold: false}, alignment: {vertical: "center"}}, v},
})

const dateRow = makeEmptyRow({
    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "right",  vertical: "center"}}, v: "Дата заполнения:"},
    [Columns.company]: {s: {font: {bold: false}, alignment: {vertical: "center"}}, v: new Date()},
})

const personRow = (v: string): RowData => makeEmptyRow({
//    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "right",  vertical: "center"}}, v: "Ответственный:"},
    [Columns.company]: {s: {font: {bold: false}, alignment: {vertical: "center"}}, v},
})

let blankRow = makeEmptyRow()
let heading1Row = makeEmptyRow()
let heading2Row = makeEmptyRow()
let heading3Row = makeEmptyRow()

heading1Row[Columns.id]                     = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Код"}
heading1Row[Columns.division]               = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Дивизион"}
heading1Row[Columns.company]                = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Предприятие"}
heading1Row[Columns.companyRegion]          = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Регион"}
heading1Row[Columns.companyCity]            = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Город"}
heading1Row[Columns.specialityCategory]     = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "УО"}
heading1Row[Columns.specialityCode]         = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "bottom"}}, v: "Направление подготовки / Специальность (НП/С)"}
heading1Row[Columns.specialityType]         = {s: {border}}
heading1Row[Columns.courseOfStudy]          = {s: {border}}
heading1Row[Columns.specialityTitle]        = {s: {border}}
heading1Row[Columns.specialityPriority]     = {s: {border}}
heading1Row[Columns.entranceYear]           = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "bottom"}}, v: "Год заключения договора"}
heading1Row[Columns.master]                 = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "bottom"}}, v: "Планируется ли заключение договора с магистром"}
heading1Row[Columns.period]                 = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Количество лет обучения"}
heading1Row[Columns.nationalProject]        = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Потребность по набору по национальным проектам"}
heading1Row[Columns.englishLevel]           = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Уровень владения английским языком"}
heading1Row[Columns.language]               = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Другой иностранный язык"}
heading1Row[Columns.contract]               = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Анкета"}
heading1Row[Columns.age]                    = {s: {border}}
heading1Row[Columns.targetOpk]              = {s: {border}}
heading1Row[Columns.graduateYear]           = {s: {border}}
heading1Row[Columns.studentPeriod]          = {s: {border}}
heading1Row[Columns.details]                = {s: {border}}
heading1Row[Columns.universityAbbreviation] = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "bottom", vertical: "bottom"}}, v: "Образовательная организация"}
heading1Row[Columns.universityTitle]        = {s: {border}}
heading1Row[Columns.universityType]         = {s: {border}}
heading1Row[Columns.region]                 = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Регион"}
heading1Row[Columns.district]               = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Округ"}

heading2Row[Columns.id]                     = {s: {border}}
heading2Row[Columns.division]               = {s: {border}}
heading2Row[Columns.company]                = {s: {border}}
heading2Row[Columns.companyRegion]          = {s: {border}}
heading2Row[Columns.companyCity]            = {s: {border}}
heading2Row[Columns.specialityCategory]     = {s: {border}}
heading2Row[Columns.specialityCode]         = {s: {border}}
heading2Row[Columns.specialityType]         = {s: {border}}
heading2Row[Columns.courseOfStudy]          = {s: {border}}
heading2Row[Columns.specialityTitle]        = {s: {border}}
heading2Row[Columns.specialityPriority]     = {s: {border}}
heading2Row[Columns.entranceYear]           = {s: {border}}
heading2Row[Columns.master]                 = {s: {border}}
heading2Row[Columns.period]                 = {s: {border}}
heading2Row[Columns.nationalProject]        = {s: {border}}
heading2Row[Columns.englishLevel]           = {s: {border}}
heading2Row[Columns.language]               = {s: {border}}
heading2Row[Columns.contract]               = {s: {border}}
heading2Row[Columns.age]                    = {s: {border}}
heading2Row[Columns.targetOpk]              = {s: {border}}
heading2Row[Columns.graduateYear]           = {s: {border}}
heading2Row[Columns.studentPeriod]          = {s: {border}}
heading2Row[Columns.details]                = {s: {border}}
heading2Row[Columns.universityAbbreviation] = {s: {border}}
heading2Row[Columns.universityTitle]        = {s: {border}}
heading2Row[Columns.universityType]         = {s: {border}}
heading2Row[Columns.region]                 = {s: {border}}
heading2Row[Columns.district]               = {s: {border}}

heading3Row[Columns.id]                     = {s: {border}}
heading3Row[Columns.division]               = {s: {border}}
heading3Row[Columns.company]                = {s: {border}}
heading3Row[Columns.companyRegion]          = {s: {border}}
heading3Row[Columns.companyCity]            = {s: {border}}
heading3Row[Columns.specialityCategory]     = {s: {border}}
heading3Row[Columns.specialityCode]         = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Код"}
heading3Row[Columns.specialityType]         = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "УП"}
heading3Row[Columns.courseOfStudy]          = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Форма обучения"}
heading3Row[Columns.specialityTitle]        = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "НП/С"}
heading3Row[Columns.specialityPriority]     = {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Тип"}
heading3Row[Columns.entranceYear]           = {s: {border}}
heading3Row[Columns.master]                 = {s: {border}}
heading3Row[Columns.period]                 = {s: {border}}
heading3Row[Columns.nationalProject]        = {s: {border}}
heading3Row[Columns.englishLevel]           = {s: {border}}
heading3Row[Columns.language]               = {s: {border}}
heading3Row[Columns.contract]               = {s: {border, font: {bold: true}, alignment: {wrapText: true,  horizontal: "center", vertical: "center"}}, v: "Заключение договора"}
heading3Row[Columns.age]                    = {s: {border, font: {bold: true}, alignment: {wrapText: true,  horizontal: "left",   vertical: "center"}}, v: "Возраст"}
heading3Row[Columns.targetOpk]              = {s: {border, font: {bold: true}, alignment: {wrapText: true,  horizontal: "left",   vertical: "center"}}, v: "Целевое обучение ОПК"}
heading3Row[Columns.graduateYear]           = {s: {border, font: {bold: true}, alignment: {wrapText: true,  horizontal: "left",   vertical: "center"}}, v: "Год выпуска"}
heading3Row[Columns.studentPeriod]          = {s: {border, font: {bold: true}, alignment: {wrapText: false, horizontal: "left",   vertical: "center"}}, v: "Срок обучения"}
heading3Row[Columns.details]                = {s: {border, font: {bold: true}, alignment: {wrapText: true,  horizontal: "center", vertical: "center"}}, v: "Прочее"}
heading3Row[Columns.universityAbbreviation] = {s: {border, font: {bold: true}, alignment: {wrapText: true,  horizontal: "left",   vertical: "center"}}, v: "Аббревиатура"}
heading3Row[Columns.universityTitle]        = {s: {border, font: {bold: true}, alignment: {wrapText: true,  horizontal: "center", vertical: "center"}}, v: "Название"}
heading3Row[Columns.universityType]         = {s: {border, font: {bold: true}, alignment: {wrapText: true,  horizontal: "center", vertical: "center"}}, v: "Тип"}
heading3Row[Columns.region]                 = {s: {border}}
heading3Row[Columns.district]               = {s: {border}}



const footer = (data: RowData[]): RowData => ({
    [Columns.id]: "",
    [Columns.division]: "",
    [Columns.company]: "",
    [Columns.companyRegion]: "",
    [Columns.companyCity]: "",
    [Columns.specialityCategory]: "",
    [Columns.specialityCode]: "",
    [Columns.specialityType]: "",
    [Columns.courseOfStudy]: "",
    [Columns.specialityTitle]: "",
    [Columns.specialityPriority]: {s: {font: {bold: true}}, v: "ИТОГО"},
    [Columns.entranceYear]: "",
    [Columns.master]: "",
    [Columns.period]: "",
    [Columns.nationalProject]: "",
    [Columns.englishLevel]: "",
    [Columns.language]: "",
    [Columns.contract]: "",
    [Columns.age]: "",
    [Columns.graduateYear]: "",
    [Columns.studentPeriod]: "",
    [Columns.universityAbbreviation]: "",
    [Columns.universityTitle]: "",
    [Columns.universityType]: "",
    [Columns.region]: "",
    [Columns.district]: "",
})

type Data = {
    demand: DemandList
    divisions: Divisions
    regions: Regions
    specialities: Specialities
    universityBranches: UniversityBranches
    meta: {
        company: string
        user: User
    }
}

const composeData = ({
    demand,
    divisions,
    regions,
    specialities,
    universityBranches,
}: Data): RowData[] => {
    let rawResult: RowData[] = []

    demand.map(({
        id,
        companyId,
        courseOfStudyName,
        specialityId,
        regionId,
        universityBranchId,
        students,
        nationalProjectName,
        englishLevelName,
        languageName,
    }: Demand) => {
        const {
            division,
            company,
            region,
            city,
        } = getCompany(divisions, companyId)

        const {
            code,
            typeName,
            title,
            priority,
            category,
            period,
        } = getSpeciality(specialities, specialityId)

        const {
            name: regionName,
            district: regionDistrict,
        } = getRegion(regions, regionId)

        const {
            abbreviation: universityAbbreviation,
            title: universityTitle,
            type: universityType,
//            specialitiesOpk,
        } = getUniversityBranch(universityBranches, universityBranchId)

        const targetOpk = getCompany(divisions, companyId).isOpk/* && specialitiesOpk.has(specialityId)*/;

        students.map(({
            id: studentId,
            age,
            period: studentPeriod,
            details,
            contractTitle,
            entranceYear,
            graduateYear,
            master,
        }: Student) => {
            let row = makeEmptyRow({
                [Columns.id]: lpad(id) + "." + lpad(studentId),
                [Columns.period]: period,
                [Columns.nationalProject]: nationalProjectName,
                [Columns.englishLevel]: englishLevelName,
                [Columns.language]: languageName,
                [Columns.courseOfStudy]: courseOfStudyName,
                [Columns.contract]: contractTitle,
                [Columns.age]: age,
                [Columns.targetOpk]: targetOpk ? "Целевое обучение ОПК" : "",
                [Columns.entranceYear]: entranceYear,
                [Columns.graduateYear]: graduateYear,
                [Columns.studentPeriod]: studentPeriod,
                [Columns.master]: master ? "Да" : "Нет",
                [Columns.details]: details,
                [Columns.division]: division,
                [Columns.company]: company,
                [Columns.companyRegion]: region,
                [Columns.companyCity]: city,
                [Columns.specialityCategory]: category,
                [Columns.specialityCode]: code,
                [Columns.specialityType]: typeName,
                [Columns.specialityTitle]: title,
                [Columns.specialityPriority]: priority,
                [Columns.region]: regionName,
                [Columns.district]: regionDistrict,
                [Columns.universityAbbreviation]: universityAbbreviation,
                [Columns.universityTitle]: universityTitle,
                [Columns.universityType]: universityType,
            })

            rawResult = rawResult.concat([row])
        })
    })

    return rawResult
}

export const studentsWs = (data: Data): SheetData => {
    console.time(__filename)

    const sheetData = composeData(data)

    const sheetFooter = footer(sheetData)
    const title = "Студенты с договорами"

    let sheet = composeSheetData(composeRow(title1Row())
        .concat(composeRow(title2Row()))
        .concat(composeRow(companyRow(data.meta.company)))
        .concat(composeRow(dateRow))
        .concat(composeRow(personRow(data.meta.user.executor.fullName)))
        .concat(composeRow(blankRow))
        .concat(composeRow(heading1Row))
        .concat(composeRow(heading2Row))
        .concat(composeRow(heading3Row))
        .concat(sheetData.map(a => Object.keys(a).map(b => a[b])))
        .concat(composeRow(sheetFooter))
    )

    sheet["!merges"] = merges.map(m => ({
        s: {c: m[0], r: m[1]},
        e: {c: m[2], r: m[3]},
    }))

    sheet["!cols"] = Object.keys(widths).map(column => ({wch: widths[column]}))

    let rows: RowStyle[] = []
    rows[Rows.Heading3] = {hpt: 31}

    sheet["!rows"] = rows

    console.timeEnd(__filename)

    return {
        sheet,
        title,
        filter: {
            columns: Object.keys(sheetFooter).length,
            row: Rows.Heading3,
        },
    }
}
